import {
  FALLBACK_TIMEZONE,
  deviceTimezone,
  isValidTimezone,
} from "@/shared/lib/zonedTime";
import React, { createContext, useContext, useMemo, ReactNode } from "react";

/**
 * The timezone every wall-clock value in the app is rendered and interpreted
 * in. It is supplied by whoever owns the tenant context (the authenticated
 * shell reads it from practice settings, public booking from the practice's
 * public info), never sniffed from the device.
 */
interface TimezoneContextValue {
  timezone: string;
}

const TimezoneContext = createContext<TimezoneContextValue | undefined>(
  undefined
);

interface TimezoneProviderProps {
  /** IANA identifier, e.g. `Europe/Zurich`. Invalid or missing falls back. */
  timezone: string | null | undefined;
  children: ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({
  timezone,
  children,
}) => {
  const value = useMemo(
    () => ({
      timezone: isValidTimezone(timezone) ? timezone : FALLBACK_TIMEZONE,
    }),
    [timezone]
  );

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

/**
 * The active display timezone.
 *
 * Falls back rather than throwing so isolated components and tests render
 * without a provider; the fallback matches the product's default tenant
 * timezone, so an unwrapped subtree degrades to the same result instead of
 * silently picking up the device timezone.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useDisplayTimezone = (): string =>
  useContext(TimezoneContext)?.timezone ?? FALLBACK_TIMEZONE;

/**
 * The device timezone when it differs from the display timezone, otherwise
 * `null`. Surfaces a "you are not where these times are" notice, which is the
 * only way a user working abroad can tell the two apart.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useDeviceTimezoneMismatch = (): string | null => {
  const timezone = useDisplayTimezone();
  return useMemo(() => {
    const device = deviceTimezone();
    if (device === timezone) return null;
    // Different identifiers can still mean the same clock (Europe/Zurich vs
    // Europe/Berlin). Only warn when the wall clock actually differs.
    const now = new Date();
    const at = (tz: string) =>
      new Intl.DateTimeFormat("en", {
        timeZone: tz,
        dateStyle: "short",
        timeStyle: "short",
      }).format(now);
    try {
      return at(device) === at(timezone) ? null : device;
    } catch {
      return null;
    }
  }, [timezone]);
};
